import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/**
 * Extracted from CategoryConsole.js (component-split refactor, round 4).
 * Segmentation tab: level selector, spend bar chart, and detail table.
 * `selectedLevel` stays controlled by the parent because the Hierarchy tab
 * and the CSV/PDF exports share the same level state.
 */
export function SegmentationTab({ categoryLevelSegmentation, selectedLevel, setSelectedLevel, cs }) {
  return (
    <TabsContent value="segmentation">
      <Card>
        <CardHeader>
          <CardTitle>Category Level Segmentation</CardTitle>
          <div className="flex gap-2 mt-4">
            {['level_1', 'level_2', 'level_3', 'level_4'].map((level) => (
              <Button
                key={level}
                variant={selectedLevel === level ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedLevel(level)}
                data-testid={`segmentation-${level}`}
              >
                Level {level.split('_')[1]}
                {categoryLevelSegmentation?.level_counts?.[level] && (
                  <span className="ml-2 text-xs opacity-70">
                    ({categoryLevelSegmentation.level_counts[level]})
                  </span>
                )}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {categoryLevelSegmentation?.[selectedLevel]?.length > 0 ? (
            <div className="space-y-4">
              {/* Bar Chart for selected level */}
              <ResponsiveContainer width="100%" height={Math.max(280, categoryLevelSegmentation[selectedLevel].slice(0, 10).length * 32)}>
                <BarChart data={categoryLevelSegmentation[selectedLevel].slice(0, 10)} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => `${cs}${(v/1000000).toFixed(1)}M`} />
                  <YAxis
                    dataKey="category"
                    type="category"
                    width={160}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => v?.length > 22 ? v.slice(0, 22) + '...' : v}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      name === 'spend' ? `${cs}${value.toLocaleString()}` : value,
                      name === 'spend' ? 'Total Spend' : name
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="spend" fill="hsl(var(--primary))" name="Total Spend" />
                </BarChart>
              </ResponsiveContainer>

              {/* Detail Table */}
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="segmentation-detail-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Category</th>
                      <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Spend</th>
                      <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">% of Total</th>
                      <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Transactions</th>
                      <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Suppliers</th>
                    </tr>
                  </thead>
                  <tbody>
                    <>{categoryLevelSegmentation[selectedLevel].map((cat, idx) => (
                      <tr key={idx} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="py-1.5 px-3">
                          <span className="font-medium">{cat.category}</span>
                          {cat.parent && (
                            <span className="text-xs text-muted-foreground ml-2">({cat.parent})</span>
                          )}
                        </td>
                        <td className="py-1.5 px-3 text-right font-mono">{cs}{cat.spend?.toLocaleString()}</td>
                        <td className="py-1.5 px-3 text-right">{cat.spend_percentage?.toFixed(1)}%</td>
                        <td className="py-1.5 px-3 text-right">{cat.transaction_count}</td>
                        <td className="py-1.5 px-3 text-right">{cat.supplier_count}</td>
                      </tr>
                    ))}</>
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No category data available for this level</p>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}
